import React, { useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { FaEdit, FaPlusCircle, FaTrash } from 'react-icons/fa';
import Select from 'react-select';
import Swal from 'sweetalert2';
import { useAppContext } from '../context/AppContext';
import SessionsDropdown from '../Components/SessionsDropDown';
import { calculateAge } from '../utils/ageCalculator';

const TabContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: stretch;
  width: 100%;
  background-color: rgb(105, 105, 105);
  padding-left: ${props => props.theme.spacing.medium};
  padding-right: ${props => props.theme.spacing.medium};
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Selector = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const NewSessionLabel = styled.h1`
  color: ${props => props.theme.colors.primary};
`;

const SectionTitle = styled.h3`
  color: ${props => props.theme.colors.primary};
  margin-bottom: ${props => props.theme.spacing.medium};
`;

// const AthleteIcon = styled(FaUser)`
//   font-size: 2rem;
//   color: ${props => props.theme.colors.primary};
//   margin-bottom: ${props => props.theme.spacing.small};
// `;

const IconButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.colors.primary};
  font-size: 1.5rem;
  cursor: pointer;
`;

const AthleteInfoContainer = styled.div`
  display: flex;
  justify-content: space-around;
  width: 100%;
  margin-right: ${props => props.theme.spacing.large};
  margin-left: ${props => props.theme.spacing.large};
`;

const AthleteInfoItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const InfoLabel = styled.span`
  font-size: '1.4rem';
  color: ${props => props.theme.colors.text};
  font-weight: bold;
  margin-right: ${props => props.theme.spacing.medium};
  margin-left: ${props => props.theme.spacing.medium};
`;

const InfoValue = styled.span`
  font-size: 0.85rem;
  color: ${props => props.theme.colors.text};
  opacity: 0.7;
  margin-right: ${props => props.theme.spacing.medium};
  margin-left: ${props => props.theme.spacing.medium};
`;

const customSelectStyles = {
  control: (provided) => ({
    ...provided,
    backgroundColor: 'rgb(71, 71, 71)',
    borderColor: 'rgb(71, 71, 71)',
    borderRadius: '20px',
    padding: '0.25rem',
    color: 'white',
    width: '15rem',
  }),
  singleValue: (provided) => ({
    ...provided,
    color: 'white',
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? '#FDBA31' : 'rgb(71, 71, 71)',
    color: state.isSelected ? 'black' : 'white',
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: 'rgb(71, 71, 71)',
  }),
};

function SingleAthleteTab() {
  const {
    setView,
    currentCampus,
    currentProgram,
    currentGender,
    currentEvalCount,
    athleteNamesList,
    athlete,
    setAthlete,
    captureModeIsActive,
    currentAthleteEvaluations,
    currentAthleteSessionIndex,
    updateAthleteList,
    loadAthleteEvaluations,
    currentDataType,
    flask_post,
    setGroupAnalysisObject,
  } = useAppContext();

  const handleAthleteSelectionChange = useCallback(async (selectedOption) => {
    try {
      const response = await flask_post('LoadAthlete', [selectedOption.value]);
      const newAthlete = response.data;
      setAthlete(newAthlete)
      loadAthleteEvaluations(newAthlete, currentDataType);
    } catch (error) {
      console.error('Error loading athlete:', error);
      Swal.fire('Error', 'Failed to load athlete data', 'error');
    }
  }, [flask_post, setAthlete, loadAthleteEvaluations, currentDataType]);

  const handleAthleteDelete = useCallback(async () => {
    if (!athlete) return;

    const result = await Swal.fire({
      title: 'Confirm athlete deletion?',
      text: 'This action is not reversible',
      showCancelButton: true,
      confirmButtonText: 'Yes',
      cancelButtonText: 'No',
      confirmButtonColor: "#FDBA31",
    });

    if (result.isConfirmed) {
      try {
        await flask_post('DeleteAthlete', [athlete]);
        
        Swal.fire('Deleted!', 'The athlete has been deleted.', 'success');
        setAthlete(null);
        setGroupAnalysisObject({});
        updateAthleteList(currentCampus, currentProgram, currentGender, currentEvalCount, currentDataType, null);
      } catch (error) {
        console.error('Error deleting athlete:', error);
        Swal.fire('Error', 'Failed to delete athlete', 'error');
      }
    }
  }, [athlete, flask_post, setAthlete, updateAthleteList, currentCampus, currentProgram, currentGender, currentEvalCount, currentDataType, setGroupAnalysisObject]);

  const getAthleteInfo = useCallback((field) => {
    if (!captureModeIsActive && currentAthleteEvaluations.length > 0) {
      const currentEvaluation = currentAthleteEvaluations[currentAthleteSessionIndex];
      return currentEvaluation?.athleteProfileForTest[field];
    } else if (athlete) {
      if (field === 'age') {
        return calculateAge(new Date(athlete.dob), new Date(), 1);
      }
      return athlete[field];
    }
    return ' - ';
  }, [captureModeIsActive, currentAthleteEvaluations, currentAthleteSessionIndex, athlete]);

  const selectOptions = useMemo(() => athleteNamesList.map(name => ({ value: name, label: name })), [athleteNamesList]);

  return (
    <TabContainer>
      <Section>
        <SectionTitle>{`Athlete Name`}</SectionTitle>
        <Selector>
          <InfoLabel>{athleteNamesList.length}</InfoLabel>
          <Select
            options={selectOptions}
            value={athlete ? { value: athlete.name, label: athlete.name } : null}
            onChange={handleAthleteSelectionChange}
            styles={customSelectStyles}
            isSearchable={true}
          />
          <div>
            <IconButton onClick={() => setView('athleteAdminEdit')}>
              <FaEdit />
            </IconButton>
            <IconButton onClick={() => setView('athleteAdminCreate')}>
              <FaPlusCircle />
            </IconButton>
            <IconButton onClick={handleAthleteDelete}>
              <FaTrash />
            </IconButton>
          </div>
        </Selector>
      </Section>

      {!captureModeIsActive ?
      <Section>
        <SectionTitle>Athlete Sessions</SectionTitle>
        <SessionsDropdown />
      </Section>
      : <NewSessionLabel>{`New Session`}</NewSessionLabel>}

      <Section>
        <SectionTitle>{captureModeIsActive ? 'Current Athlete Info' : 'Athlete Info during Session'}</SectionTitle>
        <AthleteInfoContainer>
          <AthleteInfoItem>
            <InfoLabel>Campus</InfoLabel>
            <InfoValue>{getAthleteInfo('campus')}</InfoValue>
          </AthleteInfoItem>
          <AthleteInfoItem>
            <InfoLabel>Age</InfoLabel>
            <InfoValue>{getAthleteInfo('age')}</InfoValue>
          </AthleteInfoItem>
          <AthleteInfoItem>
            <InfoLabel>Gender</InfoLabel>
            <InfoValue>{getAthleteInfo('gender')}</InfoValue>
          </AthleteInfoItem>
          <AthleteInfoItem>
            <InfoLabel>Program</InfoLabel>
            <InfoValue>{getAthleteInfo('program').replace(/ ATHlete/g, "")}</InfoValue>
          </AthleteInfoItem>
          <AthleteInfoItem>
            <InfoLabel>Group</InfoLabel>
            <InfoValue>{getAthleteInfo('group')}</InfoValue>
          </AthleteInfoItem>
        </AthleteInfoContainer>
      </Section>
    </TabContainer>
  );
}

export default React.memo(SingleAthleteTab);